import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  Fade, 
  Grow,
  Zoom,
  Dialog, 
  DialogContent, 
  useTheme,
  IconButton,
  alpha,
  styled,
  Fab,
  Pagination,
  Tooltip,
  Button,
  DialogActions,
  DialogTitle
} from '@mui/material';
import { 
  Close as CloseIcon,
  ArrowBackIos as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Videocam as VideocamIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '300px',
  
  '& .loading-text': {
    marginTop: '16px',
    color: theme.palette.text.secondary,
  }
}));

const GalleryHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
}));

const GalleryTitle = styled(Typography)(() => ({
  fontWeight: 500,
  letterSpacing: '-0.01em',
  color: 'rgba(255, 255, 255, 0.92)'
}));

const ImageGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: '16px',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '8px',
  },
}));

// Mismo tratamiento que la cuadricula de imagenes: relleno neutral que reserva
// el sitio, sin sombra —sobre casi negro no eleva nada— y sin desplazamientos
// al pasar el puntero.
const ImageContainer = styled(Box)(() => ({
  position: 'relative',
  borderRadius: 8,
  overflow: 'hidden',
  cursor: 'pointer',
  aspectRatio: '9/16',
  backgroundColor: 'rgba(255, 255, 255, 0.06)',
  '&:hover .image-overlay, &:focus-within .image-overlay': { opacity: 1 },
  '&:hover .acciones-video, &:focus-within .acciones-video': { opacity: 1 },
  // En tactil no hay hover, asi que borrar queda alcanzable igual.
  '@media (hover: none)': { '& .acciones-video': { opacity: 1 } }
}));

const VideoThumbnail = styled('img')(() => ({
  maxWidth: '100%',
  maxHeight: '100%',
  width: 'auto',
  height: 'auto',
  objectFit: 'contain'
}));

const VideoOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 0.3s ease',
  '&.image-overlay': {
    opacity: 0,
  },
}));

const PlayButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.8),
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
  },
}));

const EmptyState = styled(Box)(() => ({
  padding: '48px 0',
  maxWidth: '46ch',

  '& h2': {
    marginBottom: '8px',
    fontSize: '1.125rem',
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 0.92)'
  },

  '& p': {
    fontSize: '0.9375rem',
    color: 'rgba(255, 255, 255, 0.56)'
  }
}));

const ScrollTopButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(4),
  right: theme.spacing(4),
  opacity: 0,
  visibility: 'hidden',
  transition: 'opacity 0.3s, visibility 0.3s, transform 0.3s',
  zIndex: 9,
  '&.visible': {
    opacity: 1,
    visibility: 'visible',
    transform: 'translateY(0)'
  },
  [theme.breakpoints.down('sm')]: {
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  }
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  '& .MuiPagination-ul': {
    justifyContent: 'center',
  }
}));

const LightboxContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  cursor: 'pointer',
  userSelect: 'none',
  // Mejorar manejo táctil para dispositivos móviles
  touchAction: 'pan-y',
  WebkitTapHighlightColor: 'transparent',
  // Asegurar que ocupe todo el espacio disponible
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1)
  }
}));

const LightboxControls = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  left: 0,
  padding: theme.spacing(2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  zIndex: 10,
  opacity: 0,
  transition: 'opacity 0.3s ease-in-out',
  '&.visible': {
    opacity: 1
  },
  // Estilos específicos para dispositivos móviles
  [theme.breakpoints.down('sm')]: {
    opacity: 1, // Siempre visible en móviles
    padding: theme.spacing(1.5),
    '& .MuiIconButton-root': {
      width: '40px',
      height: '40px',
      backgroundColor: alpha(theme.palette.common.black, 0.6),
    }
  }
}));

const ImageCounter = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  backgroundColor: alpha(theme.palette.common.black, 0.5),
  padding: theme.spacing(0.5, 1.5),
  borderRadius: theme.shape.borderRadius,
  fontSize: '0.875rem',
  fontWeight: 500,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    padding: theme.spacing(0.25, 1),
  }
}));

const VideoGallery = ({ videos: propVideos, loading: propLoading, error: propError, profileId }) => {
  const [videos, setVideos] = useState(propVideos || []);
  const [loading, setLoading] = useState(propLoading !== undefined ? propLoading : true);
  const [error, setError] = useState(propError || null);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const lightboxRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const controlsTimeout = useRef(null);
  const galleryTitleRef = useRef(null);
  const theme = useTheme();
  // Usamos useAuth para obtener el rol del usuario
  const { user, userRole } = useAuth();
  
  // Verificar si el usuario es administrador o propietario del video
  const canDeleteVideo = useCallback((video) => {
    if (!user) return false;
    return user.id === video.user_id || userRole === 'admin';
  }, [user, userRole]);
  
  // Scroll al inicio
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  // Función para generar una miniatura genérica para videos
  // En lugar de intentar cargar el video (que puede fallar por CORS),
  // usaremos un icono de video genérico con un color de fondo aleatorio
  const generateGenericThumbnail = (videoId) => {
    // Crear un canvas para dibujar la miniatura
    const canvas = document.createElement('canvas');
    canvas.width = 320;
    canvas.height = 180; // Formato 16:9
    
    const ctx = canvas.getContext('2d');
    
    // Generar un color de fondo aleatorio basado en el ID del video
    // Esto asegura que el mismo video siempre tenga el mismo color
    const hash = videoId.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    // Generar un color HSL con un tono basado en el hash
    // Usamos colores oscuros para que el icono se vea bien
    const hue = Math.abs(hash) % 360;
    const bgColor = `hsl(${hue}, 70%, 30%)`;
    
    // Dibujar el fondo
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Dibujar un icono de video en el centro
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    
    // Dibujar un triángulo de reproducción
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const size = Math.min(canvas.width, canvas.height) * 0.3;
    
    ctx.beginPath();
    ctx.moveTo(centerX - size/2, centerY - size/2);
    ctx.lineTo(centerX - size/2, centerY + size/2);
    ctx.lineTo(centerX + size/2, centerY);
    ctx.closePath();
    ctx.fill();
    
    // Convertir el canvas a una URL de datos
    return canvas.toDataURL('image/jpeg', 0.9);
  };
  
  // Función para cargar un video y generar una miniatura real
  const loadVideoAndGenerateThumbnail = (video) => {
    return new Promise((resolve) => {
      // Si ya tiene miniatura, usarla
      if (video.thumbnail_url && !video.thumbnail_url.startsWith('data:')) {
        resolve(video);
        return;
      }
      
      // Crear un elemento de video para cargar el video
      const videoElement = document.createElement('video');
      
      // Configurar el video
      videoElement.muted = true;
      videoElement.playsInline = true;
      videoElement.crossOrigin = 'anonymous'; // Intentar con crossOrigin
      
      // Manejar errores de CORS
      videoElement.onerror = () => {
        console.log('Error cargando video para miniatura, usando genérica:', video.id);
        resolve({
          ...video,
          thumbnail_url: generateGenericThumbnail(video.id)
        });
      };
      
      // Cuando los metadatos estén cargados
      videoElement.onloadedmetadata = () => {
        // Avanzar un poco en el video para evitar pantallas negras
        videoElement.currentTime = 1.0;
      };
      
      // Cuando el video avance, capturar el fotograma
      videoElement.onseeked = () => {
        try {
          // Crear un canvas para capturar el fotograma
          const canvas = document.createElement('canvas');
          canvas.width = videoElement.videoWidth || 640;
          canvas.height = videoElement.videoHeight || 360;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
          
          // Convertir el canvas a una URL de datos
          const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.7);
          
          // Devolver el video con la miniatura
          resolve({
            ...video,
            thumbnail_url: thumbnailUrl
          });
        } catch (err) {
          console.error('Error generando miniatura:', err);
          resolve({
            ...video,
            thumbnail_url: generateGenericThumbnail(video.id)
          });
        }
      };
      
      // Configurar la fuente del video
      // Usar directamente la URL del video, ya que al reproducir no hay problemas de CORS
      // El problema solo ocurre cuando intentamos acceder a los datos del video para dibujar en el canvas
      videoElement.src = video.url;
      
      // Si hay error con la URL directa, intentar con el proxy
      videoElement.addEventListener('error', () => {
        console.log('Intentando cargar video a través del proxy...');
        const proxyUrl = `${window.location.origin}/api/proxy-video?url=${encodeURIComponent(video.url)}&t=${Date.now()}`;
        videoElement.src = proxyUrl;
      });
      
      // Iniciar carga del video
      videoElement.load();
      
      // Si después de 5 segundos no se ha generado la miniatura, usar una genérica
      setTimeout(() => {
        if (!video.thumbnail_url || video.thumbnail_url === generateGenericThumbnail(video.id)) {
          console.log('Timeout generando miniatura, usando genérica:', video.id);
          resolve({
            ...video,
            thumbnail_url: generateGenericThumbnail(video.id)
          });
        }
      }, 5000);
    });
  };
  
  // Usar videos proporcionados como prop o cargar desde la API
  useEffect(() => {
    if (propVideos) {
      // Generar miniaturas para los videos que no tienen
      const processVideos = async () => {
        setLoading(true);
        
        try {
          // Procesar los videos en paralelo para generar miniaturas
          const processPromises = propVideos.map(video => loadVideoAndGenerateThumbnail(video));
          const processedVideos = await Promise.all(processPromises);
          
          setVideos(processedVideos);
        } catch (error) {
          console.error('Error procesando videos:', error);
          // En caso de error, usar miniaturas genéricas
          const fallbackVideos = propVideos.map(video => ({
            ...video,
            thumbnail_url: video.thumbnail_url || generateGenericThumbnail(video.id)
          }));
          setVideos(fallbackVideos);
        } finally {
          setLoading(false);
        }
      };
      
      processVideos();
    }
  }, [propVideos]);
  
  // Filtrar videos por perfil si se proporciona un profileId
  const displayVideos = useMemo(() => {
    return videos.filter(video => !profileId || video.profile_id === profileId);
  }, [videos, profileId]);
  
  // Calcular el número total de páginas
  const totalPages = useMemo(() => {
    return Math.ceil(displayVideos.length / itemsPerPage);
  }, [displayVideos, itemsPerPage]);
  
  // Obtener los videos para la página actual
  const currentVideos = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return displayVideos.slice(startIndex, endIndex);
  }, [displayVideos, page, itemsPerPage]);

  useEffect(() => {
    const handleScroll = () => {
      // Mostrar botón cuando se ha desplazado más de 300px
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  const handleOpenLightbox = (index) => {
    setSelectedIndex(index);
    setSelectedVideo(currentVideos[index]);
    setLightboxOpen(true);
    setControlsVisible(true);
  };
  
  const handleCloseLightbox = useCallback(() => {
    // Cerrar directamente sin animación previa
    setLightboxOpen(false);
  }, []);
  
  const handlePrevVideo = useCallback(() => {
    if (selectedIndex > 0) {
      // Forzar visibilidad de controles al cambiar de video
      setControlsVisible(true);
      setSelectedIndex(selectedIndex - 1);
      setSelectedVideo(displayVideos[selectedIndex - 1]);
    }
  }, [selectedIndex, displayVideos]);
  
  const handleNextVideo = useCallback(() => {
    if (selectedIndex < displayVideos.length - 1) {
      // Forzar visibilidad de controles al cambiar de video
      setControlsVisible(true);
      setSelectedIndex(selectedIndex + 1);
      setSelectedVideo(displayVideos[selectedIndex + 1]);
    }
  }, [selectedIndex, displayVideos]);
  
  // Manejador de movimiento del ratón
  const handleMouseMove = () => {
    // Mostrar controles al mover el ratón
    setControlsVisible(true);
    
    // Ocultar controles después de 3.5 segundos de inactividad en escritorio
    // y 5 segundos en dispositivos móviles
    clearTimeout(controlsTimeout.current);
    const timeoutDuration = window.innerWidth < 600 ? 5000 : 3500;
    
    controlsTimeout.current = setTimeout(() => {
      // En móviles, solo ocultamos los controles si no estamos cambiando de imagen
      if (window.innerWidth >= 600) {
        setControlsVisible(false);
      }
    }, timeoutDuration);
  };
  
  // Manejadores de eventos táctiles para navegación por deslizamiento
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    // Mostrar controles al iniciar toque
    setControlsVisible(true);
  };
  
  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diffX = touchStartX.current - touchEndX.current;
    
    // Si el deslizamiento es significativo (más de 50px)
    if (Math.abs(diffX) > 50) {
      if (diffX > 0) {
        // Deslizamiento hacia la izquierda -> siguiente video
        handleNextVideo();
      } else {
        // Deslizamiento hacia la derecha -> video anterior
        handlePrevVideo();
      }
    } else {
      // Si es un toque simple (no deslizamiento), alternar visibilidad de controles
      setControlsVisible(!controlsVisible);
    }
  };
  
  const handleKeyDown = useCallback((e) => {
    if (!lightboxOpen) return;
    
    switch (e.key) {
      case 'ArrowLeft':
        handlePrevVideo();
        break;
      case 'ArrowRight':
        handleNextVideo();
        break;
      case 'Escape':
        handleCloseLightbox();
        break;
      default:
        break;
    }
  }, [lightboxOpen, handlePrevVideo, handleNextVideo, handleCloseLightbox]);
  
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      clearTimeout(controlsTimeout.current);
    };
  }, [handleKeyDown]);
  
  // Manejar cambio de página
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    // Desplazar hasta el título de la galería en lugar de hasta el inicio
    if (galleryTitleRef.current) {
      galleryTitleRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  const handleDeleteClick = (e, video) => {
    e.stopPropagation();
    setVideoToDelete(video);
    setDeleteDialogOpen(true);
  };
  
  const confirmDelete = async () => {
    if (!videoToDelete) return;
    
    setDeleteLoading(true);
    try {
      // Importar la función deleteVideo
      const { deleteVideo } = await import('../utils/api');
      
      // Llamar a la API para eliminar el video
      await deleteVideo(videoToDelete.id);
      
      console.log('Video eliminado correctamente:', videoToDelete.id);
      
      // Actualizar la lista de videos después de eliminar
      setVideos(prevVideos => prevVideos.filter(v => v.id !== videoToDelete.id));
      
      // Cerrar el diálogo y limpiar el estado
      setDeleteDialogOpen(false);
      setVideoToDelete(null);
    } catch (error) {
      console.error('Error al eliminar el video:', error);
      // Mostrar mensaje de error
      setError(`Error al eliminar el video: ${error.message || 'Error desconocido'}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <StyledBox>
        <CircularProgress size={50} />
        <Typography className="loading-text" variant="h6">
          Loading videos...
        </Typography>
      </StyledBox>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 4 }}>
        {error}
      </Alert>
    );
  }

  if (!displayVideos || displayVideos.length === 0) {
    return (
      <EmptyState>
        <VideocamIcon sx={{ fontSize: 60, color: 'secondary.main', mb: 2, opacity: 0.7 }} />
        <Typography variant="h2">No videos yet</Typography>
        <Typography variant="body1">
          {profileId 
            ? "This collection doesn't have any videos yet. Upload some to get started!"
            : "There are no videos in the gallery yet. Upload some to get started!"}
        </Typography>
      </EmptyState>
    );
  }

  return (
    // Sin Container propio ni Fade de 800ms: ya vive dentro del Container de
    // ProfileView y los padding se sumaban.
    <Box ref={galleryTitleRef}>
      {/* Dentro de una coleccion, el nombre y el conteo ya estan arriba y este
          titulo era un segundo <h1> repitiendolos. */}
      {!profileId && (
        <GalleryHeader>
          <GalleryTitle variant="h4" component="h1">
            All Videos
          </GalleryTitle>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.56)' }}>
            {displayVideos.length} {displayVideos.length === 1 ? 'video' : 'videos'}
          </Typography>
        </GalleryHeader>
      )}

        <ImageGrid>
          {currentVideos.map((video, index) => (
            // Sin el Grow escalonado: el ultimo video de la pagina esperaba
            // varios segundos para aparecer.
            <React.Fragment key={video.id}>
              <ImageContainer onClick={() => handleOpenLightbox(index)}>
                <Box sx={{ 
                  width: '100%', 
                  height: '100%', 
                  backgroundColor: 'black',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  <Box 
                    component="img"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    src={video.thumbnail_url || `/video-placeholder.jpg`} 
                    alt={video.title || 'Video thumbnail'}
                    loading="lazy"
                  />
                </Box>
                <VideoOverlay className="image-overlay">
                  <PlayButton>
                    <PlayArrowIcon fontSize="large" />
                  </PlayButton>
                </VideoOverlay>
                
                {canDeleteVideo(video) && (
                  <Tooltip title="Delete Video">
                    <IconButton
                      size="small"
                      // El rojo se queda: es una accion destructiva y quitarle
                      // el color la volveria indistinguible de una neutral. Lo
                      // que cambia es cuando aparece: borrar es lo que menos se
                      // hace y era el elemento mas visible de cada video.
                      color="error"
                      className="acciones-video"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        width: 32,
                        height: 32,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        opacity: 0,
                        transition: 'opacity 160ms ease',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                        '&:focus-visible': { opacity: 1 },
                        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                        zIndex: 10
                      }}
                      onClick={(e) => handleDeleteClick(e, video)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </ImageContainer>
            </React.Fragment>
          ))}
        </ImageGrid>
        
        {totalPages > 1 && (
          <PaginationContainer>
            <Pagination 
              count={totalPages} 
              page={page} 
              onChange={handlePageChange} 
              color="primary"
              size={window.innerWidth < 600 ? "small" : "medium"}
              showFirstButton
              showLastButton
            />
          </PaginationContainer>
        )}

        <ScrollTopButton 
          color="primary" 
          size="small"
          onClick={scrollToTop}
          className={showScrollTop ? 'visible' : ''}
          aria-label="scroll to top"
        >
          <KeyboardArrowUpIcon />
        </ScrollTopButton>
        
        {/* Diálogo de confirmación para eliminar video */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            ¿Eliminar video?
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              Esta acción no se puede deshacer. ¿Estás seguro de que deseas eliminar este video?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setDeleteDialogOpen(false)} 
              color="primary"
              disabled={deleteLoading}
            >
              Cancelar
            </Button>
            <Button 
              onClick={confirmDelete} 
              color="error" 
              variant="contained"
              disabled={deleteLoading}
              startIcon={deleteLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
            >
              {deleteLoading ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Diálogo de reproducción de video */}
        <Dialog 
          open={lightboxOpen} 
          onClose={handleCloseLightbox}
          maxWidth="md"
          fullWidth
          TransitionComponent={Zoom}
          TransitionProps={{
            timeout: 300
          }}
          PaperProps={{
            style: {
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              boxShadow: 'none',
              margin: 0,
              borderRadius: 8,
              overflow: 'hidden',
              height: '90vh'
            }
          }}
        >
          <LightboxContainer 
            ref={lightboxRef}
            onMouseMove={handleMouseMove}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <LightboxControls className={controlsVisible ? 'visible' : ''}>
              {/* Contador de videos - muestra la posición actual */}
              <ImageCounter>
                {selectedIndex + 1} / {displayVideos.length}
              </ImageCounter>
              
              {/* Botón de cierre */}
              <IconButton 
                onClick={handleCloseLightbox}
                sx={{ 
                  color: 'white',
                  bgcolor: alpha(theme.palette.common.black, 0.5),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.black, 0.7),
                  },
                  // Estilos específicos para dispositivos móviles
                  [theme.breakpoints.down('sm')]: {
                    width: '48px',
                    height: '48px',
                    bgcolor: alpha(theme.palette.common.black, 0.7),
                    border: '2px solid rgba(255,255,255,0.3)',
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.5rem',
                    }
                  }
                }}
                aria-label="Cerrar vista de video"
              >
                <CloseIcon />
              </IconButton>
            </LightboxControls>

            {/* Botón de navegación a video anterior - siempre visible en móvil */}
            <NavigationButton 
              onClick={handlePrevVideo}
              className={controlsVisible ? 'visible' : ''}
              sx={{ 
                left: theme.spacing(2),
                // Asegurar que sea visible en móviles incluso si es el primer video
                [theme.breakpoints.down('sm')]: {
                  display: selectedIndex > 0 ? 'flex' : 'none',
                  opacity: 1,
                  zIndex: 20
                }
              }}
              disabled={selectedIndex === 0}
              aria-label="Video anterior"
            >
              <ArrowBackIcon />
            </NavigationButton>

            {selectedVideo && (
              <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <video 
                  controls 
                  autoPlay
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '90vh',
                    boxShadow: '0 0 20px rgba(0,0,0,0.5)',
                    objectFit: 'contain'
                  }}
                >
                  <source src={selectedVideo.url} type={selectedVideo.mime_type || 'video/mp4'} />
                  Your browser does not support the video tag.
                </video>
              </Box>
            )}

            {/* Botón de navegación a siguiente video - siempre visible en móvil */}
            <NavigationButton 
              onClick={handleNextVideo}
              className={controlsVisible ? 'visible' : ''}
              sx={{ 
                right: theme.spacing(2),
                // Asegurar que sea visible en móviles incluso si es el último video
                [theme.breakpoints.down('sm')]: {
                  display: selectedIndex < displayVideos.length - 1 ? 'flex' : 'none',
                  opacity: 1,
                  zIndex: 20
                }
              }}
              disabled={selectedIndex === displayVideos.length - 1}
              aria-label="Siguiente video"
            >
              <ArrowForwardIcon />
            </NavigationButton>
          </LightboxContainer>
        </Dialog>
    </Box>
  );
};

// Definir el componente NavigationButton que faltaba
const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%) scale(0.9)',
  backgroundColor: alpha(theme.palette.background.paper, 0.3),
  color: theme.palette.common.white,
  opacity: 0,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    backgroundColor: alpha(theme.palette.background.paper, 0.6),
    transform: 'translateY(-50%) scale(1)'
  },
  '&.visible': {
    opacity: 1
  },
  // Estilos específicos para dispositivos móviles
  [theme.breakpoints.down('sm')]: {
    width: '40px',
    height: '40px',
    // Aumentar el área de toque para facilitar la interacción
    '& .MuiSvgIcon-root': {
      fontSize: '1.2rem',
    },
    // Asegurar que siempre sean visibles en móvil
    opacity: 1,
    backgroundColor: alpha(theme.palette.background.paper, 0.5),
  }
}));

// Función para comparar props y evitar re-renderizados innecesarios
const areEqual = (prevProps, nextProps) => {
  // Si el profileId cambia, debemos re-renderizar
  if (prevProps.profileId !== nextProps.profileId) return false;
  
  // Si los videos proporcionados como props cambian en cantidad, debemos re-renderizar
  if (prevProps.videos?.length !== nextProps.videos?.length) return false;
  
  // Si el estado de carga cambia, debemos re-renderizar
  if (prevProps.loading !== nextProps.loading) return false;
  
  // Si el error cambia, debemos re-renderizar
  if (prevProps.error !== nextProps.error) return false;
  
  return true;
};

export default React.memo(VideoGallery, areEqual);
